"use client";
import React, { useState, useEffect, useRef } from 'react';
import styles from './SwapCalculator.module.scss';
import Image from 'next/image';
import { TokenIcon } from '@/components/elements/TokenIcon';
import { useTransition, animated } from '@react-spring/web';
import { convertDecimalsToAmount, convertTokensToDecimals } from '@/utils/convertTokenAmount';
import { getDownloadAppUrl } from '@/utils/DownloadAppLink';
import { trackEvent } from '@/utils/eventTrackern';

interface TokenOption {
  name: string;
  mintAddress: string;
  icon?: string;
  price?: number;
  symbol?: string;
  decimals: number;
  verified?: boolean;
}

interface SwapCalculatorProps {
  currentToken: TokenOption;
  startingAmount?: number;
}

const SOLANA_TOKEN = {
  name: 'Solana',
  mintAddress: 'So11111111111111111111111111111111111111112',
  icon: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
  symbol: 'SOL',
  decimals: 9,
};
const USDC_TOKEN = {
  name: 'USD Coin',
  mintAddress: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  icon: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  symbol: 'USDC',
  decimals: 6,
};

// Use internal API route for default tokens (uses Redis cache)
const CAPPED_TOKENS_URL = `/tkn_lnd_pg_api/tokens/`;



const getRoundedAmount = (amount: number): number => {
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000];
  for (let i = 0; i < steps.length; i++) {
    if (amount <= steps[i]) return steps[i];
  }
  // If amount is larger than all steps, return the next power of 10
  return Math.pow(10, Math.ceil(Math.log10(amount)));
}

const SwapCalculator: React.FC<SwapCalculatorProps> = ({ currentToken, startingAmount }) => {
  // Determine default toToken
  const isSol = currentToken.mintAddress === SOLANA_TOKEN.mintAddress;


  const getDefaultAmount = () => {
    if (typeof startingAmount === 'number') return startingAmount;
    
    // Check if current token has price information
    const hasTokenPrice = typeof currentToken.price === 'number' && currentToken.price > 0;
    
    if (hasTokenPrice && currentToken.price && currentToken.price < 1) {
      return getRoundedAmount(1 / currentToken.price);
    }
    
    // For fallback SOL/USDC or tokens with price >= 1, use 1 as default
    return 1;
  };

  // Check if current token has price information
  const hasTokenPrice = typeof currentToken.price === 'number' && currentToken.price > 0;
  
  // Determine initial tokens - fallback to SOL/USDC if no price info
  const getInitialTokens = () => {
    if (!hasTokenPrice) {
      // Fallback to SOL/USDC swap if no price info available
      return {
        fromToken: SOLANA_TOKEN,
        toToken: USDC_TOKEN
      };
    }
    return {
      fromToken: { ...currentToken, decimals: currentToken.decimals ?? 9 },
      toToken: currentToken.mintAddress === SOLANA_TOKEN.mintAddress ? USDC_TOKEN : SOLANA_TOKEN
    };
  };

  const initialTokens = getInitialTokens();
  const [fromToken, setFromToken] = useState<TokenOption>(initialTokens.fromToken);
  const [toToken, setToToken] = useState<TokenOption>(initialTokens.toToken);
  const [fromAmount, setFromAmount] = useState<string>(() => String(getDefaultAmount()));
  const [toAmount, setToAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<'from' | 'to' | null>(null);
  const [tokenOptions, setTokenOptions] = useState<TokenOption[]>([]);
  const [tokenSymbols, setTokenSymbols] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  // Refs for dropdowns
  const fromDropdownRef = useRef<HTMLDivElement>(null);
  const toDropdownRef = useRef<HTMLDivElement>(null);

  // Track if a dropdown transition is running
  const [dropdownTransitioning, setDropdownTransitioning] = useState(false);
  
  // Track which field is being edited
  const [editingToAmount, setEditingToAmount] = useState(false);
  
  // Request prevention - prevent multiple simultaneous requests
  const [isInitialized, setIsInitialized] = useState(false);
  const requestInProgressRef = useRef(false);
  
  // Track if component is mounted
  const isMountedRef = useRef(false);
  
  // Track if values are being set programmatically (not by user input)
  const isSettingFromAmountRef = useRef(false);
  const isSettingToAmountRef = useRef(false);
  
  // Add debugging
  const requestCountRef = useRef(0);
  
  // Track initialization state
  const hasInitializedRef = useRef(false);
  const initialQuoteRequestedRef = useRef(false);
  const isPageReadyRef = useRef(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const [isTokenDataReady, setIsTokenDataReady] = useState(false);
  const tokenDataReadyRef = useRef(false);
  
  // Track user interaction state
  const hasUserInteractedRef = useRef(false);
  const lastUserInputRef = useRef<string>('');
  
  // Global request tracking to prevent double requests in Strict Mode
  const globalRequestIdRef = useRef<string>('');
  const [initialRequestMade, setInitialRequestMade] = useState(false);

  // Mark component as mounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cleanup refs on unmount
      requestInProgressRef.current = false;
      isSettingFromAmountRef.current = false;
      isSettingToAmountRef.current = false;
    };
  }, []);

  // Set default tokens on first render and when currentToken.mintAddress changes
  useEffect(() => {
    // Check if current token has price information
    const hasTokenPrice = typeof currentToken.price === 'number' && currentToken.price > 0;
    
    if (!hasTokenPrice) {
      // Fallback to SOL/USDC swap if no price info available
      setFromToken(SOLANA_TOKEN);
      setToToken(USDC_TOKEN);
    } else {
      setFromToken({ ...currentToken, decimals: currentToken.decimals ?? 9 });
      setToToken(isSol ? USDC_TOKEN : SOLANA_TOKEN);
    }
    
    setIsInitialized(false);
    setIsTokenDataReady(false);
    setInitialRequestMade(false); // Reset global request state
    
    // Reset all refs
    requestCountRef.current = 0;
    initialQuoteRequestedRef.current = false;
    tokenDataReadyRef.current = false;
    hasInitializedRef.current = false;
    isPageReadyRef.current = false;
    hasUserInteractedRef.current = false;
    lastUserInputRef.current = '';
    globalRequestIdRef.current = '';
  }, [currentToken.mintAddress, currentToken.price]);

  // Wait for page to be ready
  useEffect(() => {
    if (isPageReadyRef.current) return;
    
    const timer = setTimeout(() => {
      isPageReadyRef.current = true;
      setIsPageReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Wait for token data to be stable
  useEffect(() => {
    if (tokenDataReadyRef.current) return;
    if (!fromToken || !toToken || !fromAmount) return;
    if (!fromToken.mintAddress || !toToken.mintAddress) return;
    
    const timer = setTimeout(() => {
      tokenDataReadyRef.current = true;
      setIsTokenDataReady(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [fromToken?.mintAddress, toToken?.mintAddress, fromAmount]);

  // Initial quote request - only once when everything is ready
  useEffect(() => {
    if (!isMountedRef.current || !isPageReady || !isTokenDataReady) return;
    if (!fromToken || !toToken || !fromAmount) return;
    if (!fromToken.mintAddress || !toToken.mintAddress) return;
    if (initialQuoteRequestedRef.current || requestInProgressRef.current) return;
    if (initialRequestMade) return; // Prevent double requests in Strict Mode
    
    // Create a unique request identifier for this token combination
    const requestId = `initial-${fromToken.mintAddress}-${toToken.mintAddress}-${fromAmount}`;
    if (globalRequestIdRef.current === requestId) return; // Skip if same request already made
    
    const fetchInitialQuote = async () => {
      initialQuoteRequestedRef.current = true;
      globalRequestIdRef.current = requestId; // Mark this request as made
      setInitialRequestMade(true); // Update state to prevent future requests
      requestInProgressRef.current = true;
      requestCountRef.current++;
      setLoading(true);
      
      try {
        const preparedAmount = convertDecimalsToAmount(fromAmount, fromToken.decimals);
        const quoteUrl = `/tkn_lnd_pg_api/quote/?fromMint=${fromToken.mintAddress}&toMint=${toToken.mintAddress}&amount=${preparedAmount}`;
        const res = await fetch(quoteUrl);
        const data = await res.json();
        
        if (data && data.uiData && data.uiData.outAmount) {
          const outAmount = convertTokensToDecimals(data.uiData.outAmount, toToken.decimals);
          isSettingToAmountRef.current = true;
          setToAmount(outAmount);
          setTimeout(() => {
            isSettingToAmountRef.current = false;
          }, 100);
        } else {
          isSettingToAmountRef.current = true;
          setToAmount('');
          setTimeout(() => {
            isSettingToAmountRef.current = false;
          }, 100);
        }
      } catch {
        isSettingToAmountRef.current = true;
        setToAmount('');
        setTimeout(() => {
          isSettingToAmountRef.current = false;
        }, 100);
      } finally {
        setLoading(false);
        requestInProgressRef.current = false;
        setIsInitialized(true);
        hasUserInteractedRef.current = false; // Reset after initial request
      }
    };

    fetchInitialQuote();
  }, [isPageReady, isTokenDataReady, initialRequestMade]); // Add initialRequestMade to dependencies

  // User input quote requests - only after initial setup and on actual user interaction
  // Debounce for fromAmount input
const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
const [debouncedFromAmount, setDebouncedFromAmount] = useState(fromAmount);
const [noQuote, setNoQuote] = useState(false);

useEffect(() => {
  if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
  debounceTimeoutRef.current = setTimeout(() => {
    setDebouncedFromAmount(fromAmount);
  }, 400); // 400ms debounce
  return () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
  };
}, [fromAmount]);

  useEffect(() => {
    setNoQuote(false); // reset on new input
    if (!isMountedRef.current || !isInitialized || requestInProgressRef.current) return;
    if (!fromToken || !toToken || !debouncedFromAmount) return;
    if (!fromToken.mintAddress || !toToken.mintAddress) return;
    if (!hasUserInteractedRef.current) return;

    if (debouncedFromAmount === '0' || Number(debouncedFromAmount) === 0) {
      isSettingToAmountRef.current = true;
      setToAmount('0');
      setTimeout(() => {
        isSettingToAmountRef.current = false;
      }, 100);
      lastUserInputRef.current = `${debouncedFromAmount}-${fromToken.mintAddress}-${toToken.mintAddress}`;
      return;
    }

    const currentInput = `${debouncedFromAmount}-${fromToken.mintAddress}-${toToken.mintAddress}`;
    if (lastUserInputRef.current === currentInput) return;

    const fetchUserQuote = async () => {
      requestInProgressRef.current = true;
      requestCountRef.current++;
      setLoading(true);
      setNoQuote(false);
      try {
        const preparedAmount = convertDecimalsToAmount(debouncedFromAmount, fromToken.decimals);
        const quoteUrl = `/tkn_lnd_pg_api/quote/?fromMint=${fromToken.mintAddress}&toMint=${toToken.mintAddress}&amount=${preparedAmount}`;
        const res = await fetch(quoteUrl);
        const data = await res.json();
        if (data && data.uiData && data.uiData.outAmount) {
          const outAmount = convertTokensToDecimals(data.uiData.outAmount, toToken.decimals);
          isSettingToAmountRef.current = true;
          setToAmount(outAmount);
          setTimeout(() => {
            isSettingToAmountRef.current = false;
          }, 100);
          setNoQuote(false);
        } else if (data && data.error === 'No quotes found!' && data.code === 404) {
          isSettingToAmountRef.current = true;
          setToAmount('');
          setNoQuote(true);
          setTimeout(() => {
            isSettingToAmountRef.current = false;
          }, 100);
        } else {
          isSettingToAmountRef.current = true;
          setToAmount('');
          setNoQuote(false);
          setTimeout(() => {
            isSettingToAmountRef.current = false;
          }, 100);
        }
      } catch {
        isSettingToAmountRef.current = true;
        setToAmount('');
        setNoQuote(false);
        setTimeout(() => {
          isSettingToAmountRef.current = false;
        }, 100);
      } finally {
        setLoading(false);
        requestInProgressRef.current = false;
        lastUserInputRef.current = currentInput;
      }
    };
    fetchUserQuote();
  }, [isInitialized, debouncedFromAmount, fromToken?.mintAddress, toToken?.mintAddress]);

  // Reverse calculation when user edits toAmount
  useEffect(() => {
    if (!isMountedRef.current || !isInitialized || requestInProgressRef.current) return;
    if (!fromToken || !toToken || !toAmount || !editingToAmount) return;
    if (!fromToken.mintAddress || !toToken.mintAddress) return;
    if (!hasUserInteractedRef.current) return; // Only proceed if user has interacted
    if (isSettingToAmountRef.current) return; // Skip if programmatic change
    
    // Create a unique identifier for this reverse input
    const currentReverseInput = `reverse-${toAmount}-${toToken.mintAddress}-${fromToken.mintAddress}`;
    if (lastUserInputRef.current === currentReverseInput) return; // Skip if same input
    
    const fetchReverseQuote = async () => {
      requestInProgressRef.current = true;
      requestCountRef.current++;
      setLoading(true);
      
      try {
        const preparedAmount = convertDecimalsToAmount(toAmount, toToken.decimals);
        const quoteUrl = `/tkn_lnd_pg_api/quote/?fromMint=${toToken.mintAddress}&toMint=${fromToken.mintAddress}&amount=${preparedAmount}`;
        const res = await fetch(quoteUrl);
        const data = await res.json();
        
        if (data && data.uiData && data.uiData.outAmount) {
          const outAmount = convertTokensToDecimals(data.uiData.outAmount, fromToken.decimals);
          isSettingFromAmountRef.current = true;
          setFromAmount(outAmount);
          setTimeout(() => {
            isSettingFromAmountRef.current = false;
          }, 100);
        }
      } catch{
        // Don't update fromAmount on error for reverse calculation
      } finally {
        setLoading(false);
        requestInProgressRef.current = false;
        lastUserInputRef.current = currentReverseInput;
      }
    };

    fetchReverseQuote();
  }, [isInitialized, toAmount, editingToAmount, fromToken?.mintAddress, toToken?.mintAddress]);

  // Helper to handle dropdown open/close with transition lock
  const handleDropdownOpen = async (type: 'from' | 'to') => {
    if (dropdownTransitioning) return; // Prevent action during transition
    // If already open, close it
    if (dropdownOpen === type) {
      setDropdownOpen(null);
      setDropdownTransitioning(true);
      setTimeout(() => setDropdownTransitioning(false), 300); // match transition duration
      return;
    }
    try {
      const res = await fetch(CAPPED_TOKENS_URL);
      const data = await res.json();
      if (Array.isArray(data.tokens)) {
        const filtered = data.tokens
          .filter((t: any) => t.mint !== currentToken.mintAddress)
          .slice(0, 100)
          .map((t: any) => ({
            name: t.name,
            mintAddress: t.mint,
            icon: t.imageUri,
            symbol: t.symbol,
            decimals: t.decimals ?? 9,
          }));
        // Always include currentToken, SOL, and USDC at the top, but only once each
        const unique = [
          { ...currentToken, decimals: currentToken.decimals ?? 9 },
          SOLANA_TOKEN,
          USDC_TOKEN,
          ...filtered
        ].filter((t, i, arr) => arr.findIndex(x => x.mintAddress === t.mintAddress) === i);
        setTokenOptions(unique);
        const symbolMap: Record<string, string> = {};
        // Build symbol map only for tokens that have a symbol property
        unique.forEach(t => {
          if (t.symbol) symbolMap[t.mintAddress] = t.symbol;
        });
        setTokenSymbols(symbolMap);
      }
    } catch {}
    finally {
      setDropdownOpen(type);
      setDropdownTransitioning(true);
      setTimeout(() => setDropdownTransitioning(false), 300); // match transition duration
    }
  };

  // For dropdown, show all tokens except the one currently selected in the opposite field
  const fromDropdownOptions = tokenOptions.filter(
    (t, i, arr) => t.mintAddress !== toToken.mintAddress && arr.findIndex(x => x.mintAddress === t.mintAddress) === i
  );
  const toDropdownOptions = tokenOptions.filter(
    (t, i, arr) => t.mintAddress !== fromToken.mintAddress && arr.findIndex(x => x.mintAddress === t.mintAddress) === i
  );

  const fromDropdownTransition = useTransition(dropdownOpen === 'from', {
    from: { opacity: 0, transform: 'translateY(-16px)' },
    enter: { opacity: 1, transform: 'translateY(0)' },
    leave: { opacity: 0, transform: 'translateY(-16px)' },
    config: { tension: 300, friction: 30 },
  });
  const toDropdownTransition = useTransition(dropdownOpen === 'to', {
    from: { opacity: 0, transform: 'translateY(-16px)' },
    enter: { opacity: 1, transform: 'translateY(0)' },
    leave: { opacity: 0, transform: 'translateY(-16px)' },
    config: { tension: 300, friction: 30 },
  });

  // Click outside to close dropdown (only if outside dropdown itself)
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownOpen === 'from' && fromDropdownRef.current && !fromDropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
      if (dropdownOpen === 'to' && toDropdownRef.current && !toDropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Swap tokens handler
  const handleSwapTokens = () => {
    const prevFromToken = fromToken;
    const prevToToken = toToken;
    setFromToken(prevToToken);
    setToToken(prevFromToken);
    
    // Mark as programmatic changes to prevent triggering useEffects
    isSettingFromAmountRef.current = true;
    isSettingToAmountRef.current = true;
    setFromAmount(toAmount); // set input to previous output
    setToAmount(''); // clear output, will recalc
    // Mark user interaction to trigger recalculation
    hasUserInteractedRef.current = true;
    // Delay reset to ensure useEffects don't trigger
    setTimeout(() => {
      isSettingFromAmountRef.current = false;
      isSettingToAmountRef.current = false;
    }, 100);
    
    // Always update tokenOptions to include both tokens with latest data
    setTokenOptions((opts) => {
      // Remove both tokens if present
      const filtered = opts.filter(
        o => o.mintAddress !== prevFromToken.mintAddress && o.mintAddress !== prevToToken.mintAddress
      );
      // Add updated tokens to the front
      return [
        { ...prevToToken },
        { ...prevFromToken },
        ...filtered
      ];
    });
  };

  // When fromToken or toToken changes, update tokenOptions to ensure latest icons and data
  useEffect(() => {
    setTokenOptions((opts) => {
      // Remove both tokens if present
      const filtered = opts.filter(
        o => o.mintAddress !== fromToken.mintAddress && o.mintAddress !== toToken.mintAddress
      );
      // Add updated tokens to the front
      return [
        { ...fromToken },
        { ...toToken },
        ...filtered
      ];
    });
  }, [fromToken, toToken]);

  return (
    <div ref={containerRef} className={styles.swapCalcContainer}>
      <h2 className={styles.swapCalcTitle}>{fromToken.name} Swap Calculator</h2>
      {/* FROM ROW LABEL */}
      <div className={styles.swapCalcLabel}>Sell</div>
      {/* FROM ROW */}
      <div className={styles.swapCalcRow}>
      
        
        <div
          className={styles.swapCalcDropdown + (dropdownOpen === 'from' ? ' ' + styles.open : '')}
          onClick={() => handleDropdownOpen('from')}
        >
          {fromToken.icon && <TokenIcon src={fromToken.icon} alt={fromToken.name} verified={fromToken.verified} />}
          <span className={styles.swapCalcDropdownLabel}>{fromToken.symbol}</span>
          <Image src="/tkn_lnd_pg_assets/images/icons/chevron-down.svg" alt="dropdown" width={16} height={16} className={styles.swapCalcDropdownIcon} />
        </div>

       <div className={styles.swapCalcInputWrap}>
        <input
          className={styles.swapCalcInput}
          type="number"
          min="0"
          value={fromAmount}
          aria-label={`Amount of ${fromToken.symbol || fromToken.name} to sell`}
          onChange={e => {
            // Only mark as user input if not currently setting programmatically
            if (!isSettingFromAmountRef.current) {
              hasUserInteractedRef.current = true; // Mark user interaction
              setFromAmount(e.target.value);
              setEditingToAmount(false);
            }
          }}
          onFocus={() => {
            // Mark as user input when focused (but only if not programmatic)
            if (!isSettingFromAmountRef.current) {
              // User focused input
            }
          }}
        />
       
        </div>
        {fromDropdownTransition((style, show) =>
          show ? (
            <animated.div style={style} className={styles.swapCalcDropdownListFullWidth} ref={fromDropdownRef}>
              {fromDropdownOptions.map(token => (
                <div key={token.mintAddress} className={styles.swapCalcDropdownItem} onClick={() => { 
                  // Mark as programmatic change to prevent triggering useEffects
                  isSettingFromAmountRef.current = true;
                  setFromToken(token); 
                  setDropdownOpen(null);
                  // Mark user interaction to trigger recalculation
                  hasUserInteractedRef.current = true;
                  // Delay reset to ensure useEffects don't trigger
                  setTimeout(() => {
                    isSettingFromAmountRef.current = false;
                  }, 100);
                }}>
                  {token.icon && <TokenIcon src={token.icon} alt={token.name} />}
                  <span className={styles.swapCalcDropdownTokenName}>{token.name}</span>
                  {tokenSymbols[token.mintAddress] && (
                    <span className={styles.swapCalcDropdownTokenSymbol}>{tokenSymbols[token.mintAddress]}</span>
                  )}
                </div>
              ))}
            </animated.div>
          ) : null
        )}
      </div>
      {/* SEPARATOR */}
      <div className={styles.swapCalcSeparator}>
        <div className={styles.swapCalcLine} />
        <button
          type="button"
          className={styles.swapCalcSwapIconBtn}
          aria-label="Swap tokens"
          onClick={handleSwapTokens}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <Image src="/tkn_lnd_pg_assets/images/icons/icon_swap.svg" alt="swap" width={32} height={32} className={styles.swapCalcSwapIcon} />
        </button>
        <div className={styles.swapCalcLine} />
      </div>
      {/* TO ROW LABEL */}
      <div className={styles.swapCalcLabel}>Buy</div>
      {/* TO ROW */}
      <div className={styles.swapCalcRow}>
        <div
          className={styles.swapCalcDropdown + (dropdownOpen === 'to' ? ' ' + styles.open : '')}
          onClick={() => handleDropdownOpen('to')}
        >
          {toToken.icon && <TokenIcon src={toToken.icon} alt={toToken.name} verified={toToken.verified} />}
          <span className={styles.swapCalcDropdownLabel}>{toToken.symbol || toToken.name}</span>
          <Image src="/tkn_lnd_pg_assets/images/icons/chevron-down.svg" alt="dropdown" width={20} height={20} className={styles.swapCalcDropdownIcon} loading="lazy" />
        </div>
       <div  className={styles.swapCalcInputWrap}>
        <input
          className={`${styles.swapCalcInput} ${editingToAmount ? styles.editing : ''}`}
          type="number"
          min="0"
          value={loading ? '...' : toAmount}
          placeholder={editingToAmount ? "Enter amount" : (noQuote ? "No quotes available" : "Calculating...")}
          readOnly={!editingToAmount}
          disabled={true}
          aria-label={`Amount of ${toToken.symbol || toToken.name} to buy`}
          onChange={e => {
            // Only allow user input when editingToAmount is true
            if (editingToAmount && !isSettingToAmountRef.current) {
              hasUserInteractedRef.current = true; // Mark user interaction
              setToAmount(e.target.value);
            }
          }}
          onFocus={() => {
            // Enable editing mode when user focuses on toAmount
            if (!isSettingToAmountRef.current) {
              hasUserInteractedRef.current = true; // Mark user interaction
              setEditingToAmount(true);
            }
          }}
          onBlur={() => {
            // Disable editing mode when user leaves the input
            setTimeout(() => {
              setEditingToAmount(false);
            }, 100);
          }}
        />
       
        </div>
        {toDropdownTransition((style, show) =>
          show ? (
            <animated.div style={style} className={styles.swapCalcDropdownListFullWidth} ref={toDropdownRef}>
              {toDropdownOptions.map(token => (
                <div key={token.mintAddress} className={styles.swapCalcDropdownItem} onClick={() => { 
                  // Mark as programmatic change to prevent triggering useEffects
                  isSettingToAmountRef.current = true;
                  setToToken(token); 
                  setDropdownOpen(null);
                  // Mark user interaction to trigger recalculation
                  hasUserInteractedRef.current = true;
                  // Delay reset to ensure useEffects don't trigger
                  setTimeout(() => {
                    isSettingToAmountRef.current = false;
                  }, 100);
                }}>
                  {token.icon && <TokenIcon src={token.icon} alt={token.name} />}
                  <span className={styles.swapCalcDropdownTokenName}>{token.name}</span>
                  {tokenSymbols[token.mintAddress] && (
                    <span className={styles.swapCalcDropdownTokenSymbol}>{tokenSymbols[token.mintAddress]}</span>
                  )}
                </div>
              ))}
            </animated.div>
          ) : null
        )}
      </div>
      {/* BUY BUTTON */}
      <a
        className={styles.swapCalcBuyBtn}
        href={getDownloadAppUrl({ af_channel: 'swap_calculator', af_sub1: fromToken.symbol && fromToken.mintAddress ? `${fromToken.symbol}+${fromToken.mintAddress}` : undefined })}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={() => trackEvent({ eventName: 'SwapCalc', eventType: 'click', action: 'Download', details: { fromToken: fromToken.symbol, fromMint: fromToken.mintAddress } })}
      >
        Swap {fromToken.symbol}
      </a>
      {/* FOOTER TEXT */}
      <div className={styles.swapCalcFooter}>Price can depend on onramp service</div>
    </div>
  );
};

export default SwapCalculator;
